import once from 'lodash/once'
import throttle from 'lodash/throttle'

import auth from 'shared/plugins/auth'
import {useNotifyStore} from 'shared/stores/notify'
import compareSemverVersionNumbers from 'shared/utils/compareSemverVersionNumbers'

export function createFetchClient(fetchFunction) {
	// akin to axios, create a `.get`, `.post` method etc, re-using the shared options
	const httpMethods = ['get', 'post', 'put', 'patch', 'delete']
	return Object.fromEntries(
		httpMethods.map(methodName => [
			methodName,
			async function (url, body, options = {}) {
				const method = methodName.toUpperCase()
				return fetchFunction(method, url, body, options)
			},
		])
	)
}

export const api = createFetchClient(async function fetchFunction(method, url, body, options) {
	const fetchOptions = {
		method,
		credentials: 'include',
		...options,
		headers: {
			// for debugging requests to the api, for instance in Sentry
			'X-Bird-Client': `${import.meta.env.VUE_APP_TARGET} v${import.meta.env.VUE_APP_VERSION}`,
			...options?.headers,
		},
	}

	if (['POST', 'PUT', 'PATCH'].includes(method)) {
		if (typeof body === 'object' && !(body instanceof FormData)) {
			// Don't set `Content-Type` on `FormData` bodies,
			// since the browser sets it to `multipart/form-data` with a `boundary` automatically.
			fetchOptions.body = JSON.stringify(body)
			fetchOptions.headers['Content-Type'] = 'application/json'
		} else {
			fetchOptions.body = body
			if (!fetchOptions.headers['Content-Type'] && typeof body !== 'object') {
				fetchOptions.headers['Content-Type'] = 'text/plain'
			}
		}
	}

	// Attach auth header if user is authenticated. Don't trigger a login redirect if not.
	if (auth?.isAuthenticated.value) {
		const token = await auth.getTokenSilently()
		if (token) fetchOptions.headers.Authorization = `Bearer ${token}`
	}

	if (url[0] !== '/') url = '/' + url
	const response = await fetch(import.meta.env.VUE_APP_API + url, fetchOptions)

	verifyIfWebAppUpdateIsAvailable(response.headers.get('x-bird-server-version'))

	let responseData
	const contentType = response?.headers.get('Content-Type')
	if (contentType?.includes('json')) {
		responseData = await response.json()
	} else if (contentType?.includes('text')) {
		responseData = await response.text()
	} else if (contentType) {
		responseData = await response.blob()
	}

	if (!response.ok) {
		// eslint-disable-next-line no-console
		if (responseData) console.error(responseData)

		const statusCode = response.statusText || response.status
		const message = responseData?.message || responseData // show server's response `.message` property in the notification, if present.
		const errorDescription = `\nStatus: ${statusCode}\nMessage: "${message}"`
		const errorWithNotification = new Error(response.url + ': ' + errorDescription)
		errorWithNotification.status = response.status
		errorWithNotification.statusCode = statusCode
		errorWithNotification.notification = {
			title: `API request failed with response:`,
			message: `${errorDescription}\n\nIf the problem persists, please contact our support.`,
		}
		errorWithNotification.response = responseData
		throw errorWithNotification
	}

	return responseData
})

async function verifyIfWebAppUpdateIsAvailable(apiVersion) {
	if (import.meta.env.VUE_APP_TARGET !== 'web') return
	if (!apiVersion) return
	if (compareSemverVersionNumbers(import.meta.env.VUE_APP_VERSION, apiVersion) >= 0) return
	// When API version number is higher than current web app client version number,
	// verify if the update is also already live for the web app with the web app server.
	const hostedWebAppVersionResponse = await getHostedWebAppVersion()
	const isWebAppUpdateAvailable = hostedWebAppVersionResponse?.isUpdateAvailable
	if (isWebAppUpdateAvailable) notifyUserAboutUpdateOnce()
}

export const getHostedWebAppVersion = throttle(async function () {
	try {
		const response = await fetch(import.meta.env.VUE_APP_URL + '/version?t=' + Date.now(), {
			headers: {'X-Bird-Web-App-Version': import.meta.env.VUE_APP_VERSION},
		})
		return await response.json()
	} catch (e) {
		// eslint-disable-no-unused
	}
}, 60000)

const notifyUserAboutUpdateOnce = once(function () {
	const notify = useNotifyStore()
	notify.success({
		title: 'Version update',
		message: 'A new version is available. Please refresh or <button class="link-blue"> click to reload </button>',
		dangerouslyUseHTMLString: true,
		duration: 0, // won't close automatically
		onClick: () => {
			window.location.reload()
		},
	})
})
